import { takeLatest, put, call, all, select } from 'redux-saga/effects';
import { keyBy, get, set, uniq, flatten } from 'lodash';
import {
  getChosenProfileReviewsAction,
  getChosenProfileAgreementsAction,
  getChosenProfileProductionsAction,
  getDatesInProductionsAction,
  getArtistProductionsPrivacyAction,
  addAndUpdateContactsAction,
  getRepertoireProductionListAction,
  getRepertoireImageListAction,
  getRepertoireVideoListAction,
  updateRepertoireModalAction,
  updateArtistBannerAction,
  getArtistProductionsAction,
  getChosenProfileDataAction,
} from './actions';
// Reason: eslint not recognizing imports
// eslint-disable-next-line import/named
import { formatResponseToEntity, formatResponseToList } from '../../utils/crudUtils';
import * as API from '../../utils/API';
import { PRODUCTIONS, PROFILES_ENDPOINT, MEDIA } from '../../constants/endpointConstants';
import { enqueueSnackbarAction, useBufferValueAction } from '../App/actions';
import { MEDIA_TYPES_IDS, MEDIA_TAGS_TYPES_IDS } from '../../constants/consts';
import { selectArtistProductions, selectArtistPerformedDates } from './selectors';

export function* fetchProfileData({ payload }) {
  try {
    const profileData = yield call(API.getOne, PROFILES_ENDPOINT, payload?.id);
    const data = formatResponseToEntity(profileData);
    yield put(getChosenProfileDataAction.success(data));
  } catch (err) {
    yield put(getChosenProfileDataAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getChosenProfileDataAction.fulfill());
  }
}

export function* fetchProfileAgreements({ payload }) {
  try {
    let agreementData;
    const agencyResponse = yield call(API.getArtistAgencies, { artist_profile_id: payload?.profileId });
    const agencyIds = agencyResponse && get(get(agencyResponse, 'data'), 'data').map(x => x.id);
    if (agencyIds) {
      const agreementResponse = yield call(API.getArtistsAgreements, agencyIds);
      const agencies = get(get(agencyResponse, 'data'), 'data');
      const agreements = agreementResponse && get(get(agreementResponse, 'data'), 'data');
      agreementData = {
        agencies,
        agreements,
      };
    }

    yield put(getChosenProfileAgreementsAction.success({ data: agreementData }));
  } catch (err) {
    yield put(getChosenProfileAgreementsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getChosenProfileAgreementsAction.fulfill());
  }
}

export function* fetchProfileReviews({ payload }) {
  try {
    const response = yield call(API.getReviews, payload.id, payload.pagination, get(payload, 'query', {}));
    yield put(getChosenProfileReviewsAction.success(get(response, 'data')));
  } catch (err) {
    yield put(getChosenProfileReviewsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getChosenProfileReviewsAction.fulfill());
  }
}

export function* fetchProfileProductions({ payload }) {
  try {
    let response = yield call(API.getList, PRODUCTIONS, payload?.params || {});
    let productionsIds = get(response, 'data.data', []).map(production => production?.id);
    if (productionsIds.length === 0) {
      const params = payload?.params;
      params.queryParams.date_from = '';
      response = yield call(API.getList, PRODUCTIONS, params);
    }
    productionsIds = get(response, 'data.data', []).map(production => production?.id);
    const profileId = payload?.profileId;
    yield call(fetchProfilePerformedDates, {
      productionsIds,
      profileId,
    });
    if (payload?.withPrivacy) {
      yield call(fetchProfilePerformedPrivacyAttributes, {
        productionsIds,
        profileId,
      });
    }
    yield put(getChosenProfileProductionsAction.success(formatResponseToList(response)));
  } catch (err) {
    yield put(getChosenProfileProductionsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getChosenProfileProductionsAction.fulfill());
  }
}

export function* fetchProfileRepetorieProductions({ payload }) {
  try {
    const response = yield call(API.getList, PRODUCTIONS, payload?.params || {});
    const productionsIds = get(response, 'data.data', []).map(production => production?.id);
    if (productionsIds.length > 0 && payload?.others) {
      const imageParams = {
        queryParams: {
          media_type_id: MEDIA_TYPES_IDS.IMAGE,
          media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PRODUCTION,
          media_tag_id: productionsIds,
        },
        pagination: { limit: 50 },
      };
      yield call(getRepetorieImages, { imageParams });
      const videoParams = {
        queryParams: {
          media_type_id: MEDIA_TYPES_IDS.VIDEO,
          media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PRODUCTION,
          media_tag_id: productionsIds,
        },
        pagination: { limit: 20 },
      };

      yield call(getRepetorieVideos, { videoParams });
    }

    yield put(getRepertoireProductionListAction.success(formatResponseToList(response)));
  } catch (err) {
    yield put(getRepertoireProductionListAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getRepertoireProductionListAction.fulfill());
  }
}

function* getRepetorieImages({ imageParams }) {
  try {
    const responseImage = yield call(API.getList, MEDIA, imageParams);
    yield put(getRepertoireImageListAction.success(get(responseImage, 'data')));
  } catch (err) {
    yield put(getRepertoireImageListAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getRepertoireImageListAction.fulfill());
  }
}

function* getRepetorieVideos({ videoParams }) {
  try {
    const responseVideo = yield call(API.getList, MEDIA, videoParams);
    yield put(getRepertoireVideoListAction.success(get(responseVideo, 'data')));
  } catch (err) {
    yield put(getRepertoireVideoListAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getRepertoireVideoListAction.fulfill());
  }
}

export function* updateRepetorieModal({ payload }) {
  yield put(updateRepertoireModalAction.success(payload));
}

function* fetchProfilePerformedDates({ productionsIds, profileId }) {
  try {
    const dates = yield call(API.getList, `${PROFILES_ENDPOINT}/productions/dates`, {
      queryParams: {
        production_id: productionsIds,
        id: profileId,
      },
    });
    yield put(getDatesInProductionsAction.success(formatResponseToList(dates)));
  } catch (err) {
    yield put(getDatesInProductionsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getDatesInProductionsAction.fulfill());
  }
}

function* fetchProfilePerformedPrivacyAttributes({ productionsIds, profileId }) {
  try {
    const attributes = yield call(API.getList, `${PRODUCTIONS}/privacy`, {
      queryParams: {
        production_id: productionsIds,
        profile_id: profileId,
      },
    });
    yield put(getArtistProductionsPrivacyAction.success(formatResponseToList(attributes)));
  } catch (err) {
    yield put(getArtistProductionsPrivacyAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getArtistProductionsPrivacyAction.fulfill());
  }
}

export function* addAndUpdateContacts({ payload }) {
  try {
    const newContacts = get(payload, 'newContacts', []);
    const updatingContacts = get(payload, 'updatingContacts', []);
    const deletedContactsIds = get(payload, 'deletedContactsIds', []);
    const endpoint = get(payload, 'endpoint');
    const callback = get(payload, 'callback');
    yield all([
      ...(newContacts?.length > 0 ? [call(API.create, endpoint, { data: newContacts })] : []),
      ...(updatingContacts?.length > 0
        ? [call(API.updateOne, endpoint, updatingContacts[0]?.id, { data: updatingContacts })]
        : []),
      ...deletedContactsIds.map(deletedContactId => call(API.deleteOne, endpoint, deletedContactId)),
    ]);

    if (callback && typeof callback === 'function') payload.callback();

    yield put(
      enqueueSnackbarAction({
        message: `Success updating contacts`,
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: `Error updating contacts`,
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

export function* updateBanner({ payload }) {
  try {
    const oldBannerId = get(payload, 'oldBannerId');
    const files = get(payload, 'files');
    const data = get(payload, 'data', {});
    const callback = get(payload, 'callback');
    yield put(useBufferValueAction.trigger());
    if (oldBannerId) {
      yield call(API.deleteOne, MEDIA, oldBannerId);
    }
    const newBannerData = yield call(API.create, MEDIA, data, files);
    yield put(useBufferValueAction.success(newBannerData?.data));
    if (callback && typeof callback === 'function') payload.callback();

    yield put(
      enqueueSnackbarAction({
        message: `Success updating banner`,
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(useBufferValueAction.error(err?.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error updating banner`,
        options: {
          variant: 'error',
        },
      }),
    );
  }
}

function* getArtistProductions({ payload }) {
  try {
    const response = yield call(API.getList, PRODUCTIONS, payload?.params || {});
    const productionResponse = response?.data;
    const productions = get(productionResponse, 'data', []);
    const productionsData = yield select(selectArtistProductions());
    const productionsIds = productions.map(production => production?.id);
    if (productionsIds.length > 0) {
      const profileId = payload?.profileId;
      yield call(fetchProfilePerformedDates, {
        productionsIds,
        profileId,
      });
      const productionsDates = yield select(selectArtistPerformedDates());
      const selectedDates = keyBy(productionsDates, 'productionId');
      productions.forEach(p => {
        const allSelectedDates = uniq(flatten(Object.values(get(selectedDates, `${p?.id}.artistDates`, []))));
        set(
          p,
          'dates',
          get(p, 'dates', []).map(day => ({ ...day, performed: allSelectedDates.includes(day?.date) })),
        );
      });
    }
    if (productionsData?.data?.length > 0 && !payload?.clearData) {
      productionResponse?.data.unshift(...productionsData.data);
    }
    yield put(getArtistProductionsAction.success(productionResponse));
  } catch (err) {
    yield put(getArtistProductionsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: err?.message,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getArtistProductionsAction.fulfill());
  }
}

export default function* artistSaga() {
  yield takeLatest(getChosenProfileDataAction.TRIGGER, fetchProfileData);
  yield takeLatest(updateRepertoireModalAction.TRIGGER, updateRepetorieModal);
  yield takeLatest(getChosenProfileReviewsAction.TRIGGER, fetchProfileReviews);
  yield takeLatest(getChosenProfileAgreementsAction.TRIGGER, fetchProfileAgreements);
  yield takeLatest(getChosenProfileProductionsAction.TRIGGER, fetchProfileProductions);
  yield takeLatest(addAndUpdateContactsAction.TRIGGER, addAndUpdateContacts);
  yield takeLatest(getRepertoireProductionListAction.TRIGGER, fetchProfileRepetorieProductions);
  yield takeLatest(updateArtistBannerAction.TRIGGER, updateBanner);
  yield takeLatest(getArtistProductionsAction.TRIGGER, getArtistProductions);
}
